import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	concat,
	debounceTime,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import { goToHdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { AppPublicProjectView } from '../../../ui/components/app-public-project-view/app-public-project-view.entity';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	duplicateTimeline,
	initTimelineList,
	navigateToTimeline,
	navigateToTimelineCreate,
	openDeleteConfirmationPopup,
	refreshTimelineList,
	TimelineSelector,
	toggleTimelineStatus,
	updateIncludePublic,
} from '../../store';
import { TimelineItemEntity } from './timeline-item.entity';
import { TimelineListEntity } from './timeline-list.entity';

export type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

type TimelineOverlayDetails = {
	detailsTimelineId: number | string | undefined;
};

@Component({
	selector: 'app-timeline-list',
	templateUrl: './timeline-list.component.html',
	styleUrls: ['./timeline-list.component.scss'],
	inputs: ['projectLabel'],
})
export class TimelineListComponent extends DcBaseComponent implements OnInit {
	public paths: Array<IBreadCrumbEntry> = [];
	protected readonly DcIcons = DcIcons;
	protected readonly HdIcons = HdIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;

	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const timeline = item as TimelineItemEntity;
				this.toggleActivationStatus(timeline);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const timeline = item as TimelineItemEntity;
				this.toggleActivationStatus(timeline);
			},
			icon: ActionIcons.ActivateElement,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$!: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: TimelineListEntity;
		includePublic: boolean;
		openedOverlays: TimelineOverlayDetails;
		publicProjectDetails: AppPublicProjectView | null;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly timelineListSubject = new ReplaySubject<TimelineListEntity>(
		1
	);

	private readonly openedOverlaySubject =
		new BehaviorSubject<TimelineOverlayDetails>({
			detailsTimelineId: undefined,
		});

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly timelineSelector: TimelineSelector,
		private readonly dxStorage: DxListsStorage,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'timeline-list';
		this.dxLocalStorageKey = DcDxGrids.TimelineListStandalone;

		const targetTimeline = this.openedOverlaySubject.pipe(
			filter((val) => val.detailsTimelineId !== undefined),
			switchMap((val) =>
				this.timelineSelector.getTimeline$(val.detailsTimelineId as number)
			),
			map((e) => this.buildPublicProjectDetails(e))
		);

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.timelineSelector.getLoading$(),
			list: this.timelineListSubject,
			includePublic: this.timelineSelector.getIncludePublic$(),
			openedOverlays: this.openedOverlaySubject,
			publicProjectDetails: concat(of(null), targetTimeline),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshTimelineList());
	}

	public createNewTimeline(): void {
		this.store.dispatch(navigateToTimelineCreate());
	}

	public async navigateToTimeline(
		timeline: TimelineItemEntity,
		goToVisuTab = false
	): Promise<void> {
		if (!timeline.isInSameProject && timeline.isProjectPublic) {
			const previous = await firstValueFrom(this.openedOverlaySubject);
			return this.openedOverlaySubject.next({
				detailsTimelineId:
					previous.detailsTimelineId === timeline.id ? undefined : timeline.id,
			});
		}
		this.store.dispatch(
			navigateToTimeline({
				id: timeline.id,
				goToVisuTab,
			})
		);
	}

	public async openPublicProjectDetailsPopover(
		event: Event,
		elemId: number
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		if (previous.detailsTimelineId === elemId) {
			return this.openedOverlaySubject.next({
				...previous,
				detailsTimelineId: undefined,
			});
		}
		this.openedOverlaySubject.next({
			...previous,
			detailsTimelineId: elemId,
		});
	}

	public closeOverlay(): void {
		this.openedOverlaySubject.next({
			detailsTimelineId: undefined,
		});
	}

	public deleteTimeline(event: Event, timeline: TimelineItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			openDeleteConfirmationPopup({
				timelineId: [timeline.id],
				timelineLabel: timeline.label,
			})
		);
	}

	public duplicateTimeline(event: Event, timeline: TimelineItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			duplicateTimeline({
				timelineId: timeline.id,
			})
		);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public toggleIncludePublic(event: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: event.value,
			})
		);
	}

	public toggleActivationStatus(timeline: TimelineItemEntity): void {
		this.store.dispatch(
			toggleTimelineStatus({
				timelineId: timeline.id,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: TimelineItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}
			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: TimelineItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}
	private triggerInit(): void {
		this.store.dispatch(initTimelineList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToHdHome()),
						},
						{
							label: $localize`:i18n=@@timeline.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.timelineSelector
			.getTimelineList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.timelineListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedTimelines = await firstValueFrom(
			this.timelineSelector.getSelectedTimelines$(selectedRowKeys)
		);
		if (selectedTimelines.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					timelineId: [selectedTimelines[0].id],
					timelineLabel: selectedTimelines[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				timelineId: selectedTimelines.map((k) => k.id),
				timelineLabel: '',
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.MoveDatablock,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@timeline.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@timeline.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}

	private buildPublicProjectDetails(
		e: TimelineItemEntity | undefined
	): AppPublicProjectView {
		if (!e) {
			return AppPublicProjectView.build();
		}
		return AppPublicProjectView.build({
			projectLabel: e.projectLabel,
			pictoUrl: e.projectPictoUrl,
			elemLabel: e.label,
			elemCode: e.code,
			elemId: e.id,
			createdOn: e.creationDate,
			createdBy: e.createdByAvatar,
			updatedBy: e.updatedByAvatar,
			updatedOn: e.updateDate,
		});
	}
}
